import { z } from 'zod';

export const HelpDeskUrgencySchema = z.enum(['BAIXA', 'NORMAL', 'ALTA']);
export type HelpDeskUrgency = z.infer<typeof HelpDeskUrgencySchema>;

export const HelpDeskStatusSchema = z.enum([
  'ABERTA',
  'EM_ANDAMENTO',
  'AGUARDANDO_CLIENTE',
  'RESOLVIDA',
  'ESCALADA',
]);
export type HelpDeskStatus = z.infer<typeof HelpDeskStatusSchema>;

export const HelpDeskAuthorRoleSchema = z.enum(['CLIENT', 'OPERATOR']);
export type HelpDeskAuthorRole = z.infer<typeof HelpDeskAuthorRoleSchema>;

export const HelpDeskRecordTypeSchema = z.enum(['HEADER', 'MESSAGE']);
export type HelpDeskRecordType = z.infer<typeof HelpDeskRecordTypeSchema>;

export const HELP_DESK_HEADER_SK = 'HEADER';

export const SLA_DEADLINES_MS: Record<HelpDeskUrgency, number> = {
  ALTA: 4 * 60 * 60 * 1000,
  NORMAL: 8 * 60 * 60 * 1000,
  BAIXA: 24 * 60 * 60 * 1000,
};

export const OPEN_HELP_DESK_STATUSES: HelpDeskStatus[] = [
  'ABERTA',
  'EM_ANDAMENTO',
  'AGUARDANDO_CLIENTE',
  'ESCALADA',
];

export function calculateSlaDeadline(createdAt: string, urgency: HelpDeskUrgency): string {
  const created = new Date(createdAt).getTime();
  return new Date(created + SLA_DEADLINES_MS[urgency]).toISOString();
}

export function calculateSlaRemaining(slaDeadline: string, now: Date = new Date()): number {
  return new Date(slaDeadline).getTime() - now.getTime();
}

export function generateHelpDeskRequestId(): string {
  const suffix = globalThis.crypto.randomUUID().replace(/-/g, '').slice(0, 8).toUpperCase();
  return `HD-${suffix}`;
}

export function buildHelpDeskMessageSk(createdAt: string, messageId: string): string {
  return `MSG#${createdAt}#${messageId}`;
}

export const HelpDeskMessageSchema = z.object({
  messageId: z.string().uuid(),
  authorId: z.string().min(1),
  authorName: z.string().min(1),
  authorRole: HelpDeskAuthorRoleSchema,
  body: z.string().min(1),
  isInternal: z.boolean(),
  createdAt: z.string().datetime(),
});

export type HelpDeskMessage = z.infer<typeof HelpDeskMessageSchema>;

export const HelpDeskRequestSummarySchema = z.object({
  requestId: z.string().regex(/^HD-[A-Z0-9]{8}$/),
  subject: z.string(),
  urgency: HelpDeskUrgencySchema,
  status: HelpDeskStatusSchema,
  tenantDisplayName: z.string().optional(),
  lastMessageAt: z.string().datetime(),
  lastMessagePreview: z.string(),
  slaDeadline: z.string().datetime(),
  slaRemainingMs: z.number(),
  slaViolated: z.boolean(),
  assignedToName: z.string().optional(),
  messageCount: z.number().int().min(1),
  createdAt: z.string().datetime(),
  /** Local da solicitação, congelado na abertura (spec help-desk-equipamento). Ausente nas antigas. */
  filialNome: z.string().optional(),
  deviceId: z.string().optional(),
  equipamentoNome: z.string().optional(),
});

export type HelpDeskRequestSummary = z.infer<typeof HelpDeskRequestSummarySchema>;

export const HelpDeskThreadSchema = z.object({
  requestId: z.string(),
  tenantId: z.string(),
  tenantDisplayName: z.string().optional(),
  subject: z.string(),
  urgency: HelpDeskUrgencySchema,
  status: HelpDeskStatusSchema,
  slaDeadline: z.string().datetime(),
  assignedTo: z.string().optional(),
  assignedToName: z.string().optional(),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  resolvedAt: z.string().datetime().optional(),
  escalatedAt: z.string().datetime().optional(),
  /** Local da solicitação, congelado na abertura (spec help-desk-equipamento). Ausente nas antigas. */
  designadorId: z.string().optional(),
  designadorNome: z.string().optional(),
  filialId: z.string().optional(),
  filialNome: z.string().optional(),
  deviceId: z.string().optional(),
  equipamentoNome: z.string().optional(),
  messages: z.array(HelpDeskMessageSchema),
});

export type HelpDeskThread = z.infer<typeof HelpDeskThreadSchema>;

export const CreateHelpDeskRequestSchema = z.object({
  subject: z.string().trim().min(3).max(120),
  description: z.string().trim().min(10).max(4000),
  urgency: HelpDeskUrgencySchema,
  /** Empresa da solicitação, entre as do escopo do usuário (RN-HELP-002); ausente → empresa do JWT. */
  empresaId: z.string().trim().min(1).max(128).optional(),
  /**
   * Cascata Designador → Empresa → Filial → Equipamento (spec help-desk-equipamento). O BFF
   * confere a consistência (designador da empresa, filial da empresa, equipamento da filial).
   */
  designadorId: z.string().trim().min(1).max(128).optional(),
  filialId: z.string().trim().min(1).max(128).optional(),
  deviceId: z.string().trim().min(1).max(128).optional(),
});

export type CreateHelpDeskRequest = z.infer<typeof CreateHelpDeskRequestSchema>;

export function formatCreateHelpDeskValidationMessage(error: z.ZodError): string {
  for (const issue of error.issues) {
    const field = issue.path[0];
    if (field === 'subject') {
      if (issue.code === 'too_small') return 'Assunto deve ter pelo menos 3 caracteres.';
      if (issue.code === 'too_big') return 'Assunto deve ter no máximo 120 caracteres.';
      return 'Assunto é obrigatório.';
    }
    if (field === 'description') {
      if (issue.code === 'too_small') return 'Descrição deve ter pelo menos 10 caracteres.';
      if (issue.code === 'too_big') return 'Descrição deve ter no máximo 4000 caracteres.';
      return 'Descrição é obrigatória.';
    }
    if (field === 'urgency') {
      return 'Urgência inválida.';
    }
    if (field === 'designadorId') return 'Designador inválido.';
    if (field === 'empresaId') return 'Empresa inválida.';
    if (field === 'filialId') return 'Filial inválida.';
    if (field === 'deviceId') return 'Equipamento inválido.';
  }
  return 'Dados inválidos para nova solicitação.';
}

export const PostHelpDeskMessageSchema = z.object({
  body: z.string().min(1).max(4000),
  isInternal: z.boolean().default(false),
});

export type PostHelpDeskMessage = z.infer<typeof PostHelpDeskMessageSchema>;

export const AssignHelpDeskRequestSchema = z.object({
  assignedToUserId: z.string().min(1).optional(),
});

export type AssignHelpDeskRequest = z.infer<typeof AssignHelpDeskRequestSchema>;

export const ResolveHelpDeskRequestSchema = z.object({
  resolutionNote: z.string().max(2000).optional(),
});

export type ResolveHelpDeskRequest = z.infer<typeof ResolveHelpDeskRequestSchema>;

export const HelpDeskKpisSchema = z.object({
  abertas: z.number().int().min(0),
  emAndamento: z.number().int().min(0),
  slaViolado: z.number().int().min(0),
  resolvidas7d: z.number().int().min(0),
  tempoMedioResolucaoMinutos: z.number().int().min(0).optional(),
});

export type HelpDeskKpis = z.infer<typeof HelpDeskKpisSchema>;

export function urgencySortOrder(urgency: HelpDeskUrgency): number {
  const order: Record<HelpDeskUrgency, number> = { ALTA: 0, NORMAL: 1, BAIXA: 2 };
  return order[urgency];
}

export function compareHelpDeskSummaries(a: HelpDeskRequestSummary, b: HelpDeskRequestSummary): number {
  const urgDiff = urgencySortOrder(a.urgency) - urgencySortOrder(b.urgency);
  if (urgDiff !== 0) return urgDiff;
  if (a.slaRemainingMs !== b.slaRemainingMs) return a.slaRemainingMs - b.slaRemainingMs;
  return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
}
